<script setup lang="ts">
    import _ from 'lodash';
    import { computed, onMounted, ref } from 'vue';
    import AttributeDefEdit from './AttributeDefEdit.vue';
    import MarkdownEditorComponent from '@/shared/components/MarkdownEditorComponent.vue';
    import SqlEditControl from '@/shared/components/SqlEditControl.vue';
    import Spinner from '@/shared/components/Spinner.vue';
    import TagList from '@/shared/components/TagList.vue';
    import { requireIsAdministrator as vRequireIsAdministrator } from '@/shared/directives/requirePermission';
    import {
        deleteAccessGroupLibrary,
        getAccessGroups,
        getAllTags,
        getAttributeDefinitions,
        getLibrary,
        getScopeTypes,
        getSpecializedUIs,
        getVirtualLibraryOptions,
        patchLibrary,
        postAccessGroupLibrary,
        postVirtualLibraryOptions,
        tagLibrary,
        untagLibrary,
        validateVirtualLibraryOptions,
        type AccessGroup,
        type VirtualLibraryOptionsModel,
        type VirtualLibraryOptionsValidationResult
    } from '@/shared/helpers/api';
    import ItemMetadata from '@/shared/components/ItemMetadata.vue';
    import GovernanceInformation from '@/shared/components/GovernanceInformation.vue';
    import { validateRegex } from '@/shared/helpers/validateRegex.ts';
    import { longDate } from '@/formatters.ts';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import useBuefy from '@/shared/composables/useBuefy';
    import type { Library } from '@/shared/helpers/api/types/entities.ts';

    type LibraryEditModel = Library;

    type ItemMetadataExpose = {
        save: () => Promise<boolean>;
    };

    type ListDiff = {
        new: string[];
        del: string[];
    };

    const props = defineProps<{
        libraryName: string;
    }>();

    const emit = defineEmits<{
        done: [];
    }>();

    const { showError } = useShow();
    const buefy = useBuefy();

    const attributeEdit = ref<InstanceType<typeof AttributeDefEdit> | null>(null);
    const libraryInformationElement = ref<ItemMetadataExpose | null>(null);

    const saveLoading = ref(false);
    const loading = ref(true);
    const library = ref<Library>(createEmptyLibrary());
    const libraryEdited = ref<LibraryEditModel>(createEmptyLibrary());
    const accessGroups = ref<AccessGroup[]>([]);
    const libraryErrors = ref<string[]>([]);
    const existingTags = ref<string[] | null>(null);
    const tagText = ref('');
    const agText = ref('');
    const scopeTypes = ref<string[]>([]);
    const specializedUIs = ref<string[]>([]);
    const virtualLibraryOptions = ref<VirtualLibraryOptionsModel>(createEmptyVirtualLibraryOptions());
    const virtualLibraryOptionsValidationResult = ref<VirtualLibraryOptionsValidationResult | null>(null);
    const virtualLibraryOptionsValidationRunning = ref(false);
    const hasAttributeErrors = ref(false);

    const codeNameRegexError = computed(() => {
        return validateRegex(libraryEdited.value.codeNameRegex);
    });

    const descriptionRegexError = computed(() => {
        return validateRegex(libraryEdited.value.descriptionRegex);
    });

    const codeNameRegexErrorMessageValid = computed(() => {
        return !isRegexErrorMessageWithoutRegex(libraryEdited.value.codeNameRegex, libraryEdited.value.codeNameRegexErrorMessage);
    });

    const descriptionRegexErrorMessageValid = computed(() => {
        return !isRegexErrorMessageWithoutRegex(libraryEdited.value.descriptionRegex, libraryEdited.value.descriptionRegexErrorMessage);
    });

    const canNotSave = computed(() => {
        if (loading.value) {
            return false;
        }
        return hasAttributeErrors.value
            || !!codeNameRegexError.value
            || !!descriptionRegexError.value
            || !(codeNameRegexErrorMessageValid.value
                && descriptionRegexErrorMessageValid.value);
    });

    const filteredExistingTags = computed(() => {
        if (existingTags.value) {
            const tagSearchText = tagText.value.toLowerCase();
            return existingTags.value
                .filter(t => !libraryEdited.value.tags.includes(t))
                .filter(t => tagSearchText === '' ? true : t.toLowerCase().includes(tagSearchText))
                .sort((a, b) => a.localeCompare(b));
        } else {
            return [];
        }
    });

    const filteredAccessGroups = computed(() => {
        const accessGroupSearchText = agText.value.toLowerCase();
        return accessGroups.value
            .map(g => g.name)
            .filter(g => !libraryEdited.value.accessGroups.includes(g))
            .filter(g => g.toLowerCase().includes(accessGroupSearchText))
            .sort((a, b) => a.localeCompare(b));
    });

    const filteredScopeTypes = computed(() => {
        return scopeTypes.value.filter(x => x !== library.value.name);
    });

    const virtualLibraryCompilerError = computed(() => {
        return virtualLibraryOptionsValidationResult.value
            ? virtualLibraryOptionsValidationResult.value.compilerError
            : virtualLibraryOptions.value.compilerError;
    });

    const virtualLibraryCompilerWarning = computed(() => {
        return virtualLibraryOptionsValidationResult.value
            ? virtualLibraryOptionsValidationResult.value.compilerWarning
            : virtualLibraryOptions.value.compilerWarning;
    });

    const virtualLibrarySourceQuery = computed<string | undefined>({
        get: () => virtualLibraryOptions.value.sourceQuery ?? undefined,
        set: value => virtualLibraryOptions.value.sourceQuery = value ?? null
    });

    onMounted(async () => {
        try {
            const [
                loadedLibrary,
                attributeDefinitions,
                loadedExistingTags,
                loadedAccessGroups,
                loadedScopeTypes,
                loadedSpecializedUIs,
                loadedVirtualLibraryOptions
            ] = await Promise.all([
                getLibrary(http, props.libraryName),
                getAttributeDefinitions(http, props.libraryName),
                getAllTags(http),
                getAccessGroups(http),
                getScopeTypes(http),
                getSpecializedUIs(http),
                getVirtualLibraryOptions(http, props.libraryName)
            ]);

            library.value = loadedLibrary;
            libraryEdited.value = clone(loadedLibrary);
            libraryEdited.value.attributeDefinitions = attributeDefinitions;
            existingTags.value = loadedExistingTags;
            accessGroups.value = loadedAccessGroups;
            scopeTypes.value = loadedScopeTypes.sort();
            specializedUIs.value = loadedSpecializedUIs;
            virtualLibraryOptions.value = loadedVirtualLibraryOptions;
        } catch (err) {
            showError(err);
        } finally {
            loading.value = false;
        }
    });

    function createEmptyLibrary(): Library {
        return {
            name: '',
            description: '',
            isValid: false,
            dateCreated: '',
            dateUpdated: '',
            definition: '',
            alias: '',
            nameAlias: '',
            specializedUI: '',
            isGlobal: false,
            isVirtual: false,
            isScopeSpecific: false,
            isCaseSensitive: false,
            isReadOnly: false,
            isRelation: false,
            areNamesUpperCase: false,
            nameInIdentity: false,
            nameMayChange: false,
            nameMustBeUnique: false,
            descriptionIsRequired: false,
            codeNameRegex: '',
            codeNameRegexErrorMessage: '',
            descriptionRegex: '',
            descriptionRegexErrorMessage: '',
            scopeType: '',
            codeSets: [],
            tags: [],
            accessGroups: [],
            attributeDefinitions: []
        };
    }

    function createEmptyVirtualLibraryOptions(): VirtualLibraryOptionsModel {
        return {
            exists: false,
            scopeColumn: null,
            sourceQuery: null,
            compilerError: null,
            compilerWarning: null,
            dateUpdated: ''
        };
    }

    function clone<T>(value: T): T {
        return _.cloneDeep(value);
    }

    async function validateVirtualLibraryOptionsHandler() {
        if (virtualLibraryOptionsValidationRunning.value) return;
        virtualLibraryOptionsValidationRunning.value = true;
        try {
            const response = await validateVirtualLibraryOptions(http, virtualLibraryOptions.value);
            virtualLibraryOptionsValidationResult.value = response.data;
        } catch (ex) {
            showError(ex);
        } finally {
            virtualLibraryOptionsValidationRunning.value = false;
        }
    }

    function addNewTag() {
        buefy.dialog.prompt({
            message: 'New Tag Name:',
            inputAttrs: {
                minlength: 1,
                maxlength: 64,
                required: true
            },
            onConfirm: addTag
        });
    }

    function addTag(tag: string | null) {
        if (!libraryEdited.value.tags.includes(tag ?? '') && tag !== null) {
            libraryEdited.value.tags.push(tag);
            setTimeout(() => tagText.value = '', 100);
        }
    }

    function removeTag(tag: string) {
        libraryEdited.value.tags.splice(libraryEdited.value.tags.indexOf(tag), 1);
    }

    function removeAccessGroup(group: string) {
        libraryEdited.value.accessGroups.splice(libraryEdited.value.accessGroups.indexOf(group), 1);
    }

    function addAccessGroup(group: string | null) {
        if (!group)
            return;
        libraryEdited.value.accessGroups.push(group);
        setTimeout(() => agText.value = '', 100);
    }

    function getListDiff(current: string[], original: string[]): ListDiff {
        return {
            new: current.filter(item => !original.includes(item)),
            del: original.filter(item => !current.includes(item))
        };
    }

    async function initSave() {
        saveLoading.value = true;
        const tagDiff = getListDiff(libraryEdited.value.tags, library.value.tags);
        const agDiff = getListDiff(libraryEdited.value.accessGroups, library.value.accessGroups);
        try {
            let updateVirtualLibOptions = true;
            await attributeEdit.value?.updateAttributes();
            if (libraryEdited.value.isVirtual)
                updateVirtualLibOptions = await updateVirtualLibraryOptions();
            const updatedLib = await updateLibrary();
            const updatedTag = await updateTags(tagDiff);
            const updatedAg = await updateAg(agDiff);
            const updatedLibInfo = await updateLibInfo();

            if (updatedLib && updatedTag && updatedAg && updatedLibInfo && updateVirtualLibOptions) {
                emit('done');
            }
        } catch (err) {
            showError(err);
        } finally {
            saveLoading.value = false;
        }
    }

    async function updateTags(diff: ListDiff) {
        try {
            await Promise.all(diff.new.map(tag => tagLibrary(http, tag, library.value.name)));
            await Promise.all(diff.del.map(tag => untagLibrary(http, tag, library.value.name)));
            return true;
        } catch {
            showError('Error in updating library tags');
            return false;
        }
    }

    async function updateAg(diff: ListDiff) {
        try {
            await Promise.all(diff.new.map(ag => postAccessGroupLibrary(http, ag, library.value.name)));
            await Promise.all(diff.del.map(ag => deleteAccessGroupLibrary(http, ag, library.value.name)));
            return true;
        } catch {
            showError('Error in updating access groups');
            return false;
        }
    }

    async function updateLibInfo() {
        if (!await libraryInformationElement.value?.save()) {
            showError('Error in updating governance and documentation');
            return false;
        }
        return true;
    }

    async function updateVirtualLibraryOptions() {
        try {
            await postVirtualLibraryOptions(http, library.value.name, virtualLibraryOptions.value);
            return true;
        } catch {
            showError('Error updating virtual library options');
            return false;
        }
    }

    async function updateLibrary() {
        const {
            name,
            description,
            definition,
            codeNameRegex,
            codeNameRegexErrorMessage,
            descriptionRegex,
            descriptionRegexErrorMessage,
            alias,
            nameAlias,
            specializedUI,
            isRelation,
            isValid,
            isCaseSensitive,
            isReadOnly,
            isVirtual,
            areNamesUpperCase,
            nameInIdentity,
            nameMayChange,
            nameMustBeUnique,
            isGlobal,
            isScopeSpecific,
            scopeType,
            descriptionIsRequired
        } = libraryEdited.value;

        const command = {
            name,
            description,
            definition,
            codeNameRegex,
            codeNameRegexErrorMessage,
            descriptionRegex,
            descriptionRegexErrorMessage,
            alias,
            nameAlias,
            specializedUI,
            isRelation,
            isValid,
            isCaseSensitive,
            isReadOnly,
            isVirtual,
            areNamesUpperCase,
            nameInIdentity,
            nameMayChange,
            nameMustBeUnique,
            isGlobal,
            isScopeSpecific,
            scopeType,
            descriptionIsRequired
        };

        try {
            await patchLibrary(http, library.value.name, command);
            return true;
        } catch (err) {
            const errors = _.get(err, 'response.data.errors', []);
            libraryErrors.value = Array.isArray(errors) ? errors : [];
            showError(_.get(err, 'response.data.Message', 'unable to update library'));
            return false;
        }
    }

    function newAD() {
        attributeEdit.value?.newAD();
    }

    function cancel() {
        emit('done');
    }

    function isRegexErrorMessageWithoutRegex(regex: string | null, message: string | null) {
        return Boolean(message && !regex);
    }

    function onAttrErrorChanged(newValue: boolean) {
        hasAttributeErrors.value = newValue;
    }
</script>

<template>
    <spinner :loading="loading">
        <div class="columns">
            <h1 class="title column">
                Edit {{ library.name }}
            </h1>
            <div class="column is-clearfix is-narrow">
                <div class="field is-grouped is-pulled-right">
                    <p class="control">
                        <b-button
                            icon-left="close-circle"
                            @click="cancel">
                            Cancel
                        </b-button>
                    </p>
                    <p class="control">
                        <b-button
                            icon-left="floppy"
                            class="is-primary"
                            :disabled="canNotSave"
                            :loading="saveLoading"
                            @click="initSave">
                            Save
                        </b-button>
                    </p>
                </div>
            </div>
        </div>
        <governance-information
            v-show="!loading"
            :library-name="library.name" />
        <div
            v-if="!loading">
            <b-tabs
                type="is-boxed"
                class="block">
                <b-tab-item label="Properties">
                    <div class="column has-margin-top2">
                        <div class="columns">
                            <div class="column is-two-fifths">
                                <div class="field is-horizontal">
                                    <div class="field-label">
                                        <label class="label">Description</label>
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-input v-model="libraryEdited.description" />
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div class="field is-horizontal">
                                    <div class="field-label">
                                        <label class="label">Alias</label>
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-input v-model="libraryEdited.alias" />
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div class="field is-horizontal">
                                    <div class="field-label">
                                        <label class="label">Alias for name</label>
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-input v-model="libraryEdited.nameAlias" />
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-show="!libraryEdited.isRelation"
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        <label class="label">Regex for name</label>
                                    </div>
                                    <div class="field-body">
                                        <b-field
                                            :message="codeNameRegexError"
                                            :type="codeNameRegexError ? 'is-danger' : ''">
                                            <b-input v-model="libraryEdited.codeNameRegex" />
                                        </b-field>
                                    </div>
                                </div>
                                <div
                                    v-show="!libraryEdited.isRelation"
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        <label class="label">Name validation hint</label>
                                    </div>
                                    <div class="field-body">
                                        <b-field
                                            :message="!codeNameRegexErrorMessageValid ? 'Hint is only valid when regex is set' : undefined"
                                            :type="!codeNameRegexErrorMessageValid ? 'is-danger' : undefined">
                                            <b-input v-model="libraryEdited.codeNameRegexErrorMessage" />
                                        </b-field>
                                    </div>
                                </div>
                                <div
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        <label class="label">Regex for description</label>
                                    </div>
                                    <div class="field-body">
                                        <b-field
                                            :message="descriptionRegexError"
                                            :type="descriptionRegexError ? 'is-danger' : ''">
                                            <b-input v-model="libraryEdited.descriptionRegex" />
                                        </b-field>
                                    </div>
                                </div>
                                <div
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        <label class="label">Description validation hint</label>
                                    </div>
                                    <div class="field-body">
                                        <b-field
                                            :type="!descriptionRegexErrorMessageValid ? 'is-danger' : undefined"
                                            :message="!descriptionRegexErrorMessageValid ? 'Hint is only valid when regex is set' : undefined">
                                            <b-input v-model="libraryEdited.descriptionRegexErrorMessage" />
                                        </b-field>
                                    </div>
                                </div>
                                <div class="field is-horizontal">
                                    <div class="field-label">
                                        <label class="label">Specialised UI</label>
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-select
                                                    v-model="libraryEdited.specializedUI"
                                                    placeholder="Select specialised UI">
                                                    <option :value="null">
                                                        No specialised UI
                                                    </option>
                                                    <option
                                                        v-for="option in specializedUIs"
                                                        :key="option"
                                                        :value="option">
                                                        {{ option }}
                                                    </option>
                                                </b-select>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <p
                                    v-for="error in libraryErrors"
                                    :key="error"
                                    class="has-text-danger">
                                    {{ error }}
                                </p>
                            </div>

                            <div class="column is-one-fifth">
                                <div
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.descriptionIsRequired"
                                                    type="is-info" />
                                                <strong>Description required</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.isGlobal"
                                                    :disabled="libraryEdited.isRelation"
                                                    :title="libraryEdited.isRelation ? 'Cannot be changed for relation library' : undefined"
                                                    type="is-info" />
                                                <strong>Is global</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-show="!libraryEdited.isGlobal"
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        <label class="label">Scope type</label>
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-select
                                                    v-model="libraryEdited.scopeType"
                                                    :disabled="libraryEdited.isRelation"
                                                    :title="libraryEdited.isRelation ? 'Cannot be changed for relation library' : undefined"
                                                    placeholder="Select scope type">
                                                    <option
                                                        v-for="st in filteredScopeTypes"
                                                        :key="st"
                                                        :value="st">
                                                        {{ st }}
                                                    </option>
                                                </b-select>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-show="!libraryEdited.isGlobal && !libraryEdited.isRelation"
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.isScopeSpecific"
                                                    type="is-info" />
                                                <strong>Is scope specific</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-show="!libraryEdited.isRelation"
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.isCaseSensitive"
                                                    type="is-info" />
                                                <strong>Is case sensitive</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-show="!libraryEdited.isRelation"
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.isVirtual"
                                                    :disabled="!libraryEdited.isGlobal"
                                                    type="is-info" />
                                                <strong>Is virtual</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.isReadOnly"
                                                    type="is-info" />
                                                <strong>Is read only</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-show="!libraryEdited.isRelation"
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.nameInIdentity"
                                                    type="is-info" />
                                                <strong>Name in identity</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-show="!libraryEdited.isRelation"
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.nameMayChange"
                                                    :disabled="libraryEdited.nameInIdentity"
                                                    title="Can only be set if Name is not part of identity"
                                                    type="is-info" />
                                                <strong>Name may be changed</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.nameMustBeUnique"
                                                    type="is-info" />
                                                <strong>Name must be unique</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-show="!libraryEdited.isRelation"
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.areNamesUpperCase"
                                                    type="is-info" />
                                                <strong>Uppercase names</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-show="libraryEdited.isRelation"
                                    class="field is-horizontal">
                                    <div class="field-label">
                                        &nbsp;
                                    </div>
                                    <div class="field-body">
                                        <div class="field">
                                            <p class="control">
                                                <b-checkbox
                                                    v-model="libraryEdited.isRelation"
                                                    :disabled="true"
                                                    title="Cannot be changed"
                                                    type="is-info" />
                                                <strong>Is relation</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="column">
                                <div class="columns is-multiline">
                                    <div class="column is-full">
                                        <h2 class="title is-5">
                                            Library groups
                                        </h2>
                                        <tag-list
                                            :icon-class="'mdi mdi-trash-can'"
                                            :tags="libraryEdited.tags"
                                            @click-tag="removeTag" />
                                        <b-autocomplete
                                            v-model="tagText"
                                            :data="filteredExistingTags"
                                            expanded
                                            placeholder="select existing or add new library group"
                                            :open-on-focus="true"
                                            @select="addTag">
                                            <template #header>
                                                <a @click="addNewTag">
                                                    <span> Add new library group </span>
                                                </a>
                                            </template>
                                            <template #empty>
                                                No existing library groups found
                                            </template>
                                        </b-autocomplete>
                                    </div>

                                    <div class="column">
                                        <p class="title is-5">
                                            Access groups
                                        </p>
                                        <tag-list
                                            v-require-is-administrator
                                            :icon-class="'mdi mdi-trash-can'"
                                            type="is-danger"
                                            :tags="libraryEdited.accessGroups"
                                            @click-tag="removeAccessGroup" />
                                        <b-field grouped>
                                            <b-autocomplete
                                                v-model="agText"
                                                :data="filteredAccessGroups"
                                                expanded
                                                placeholder="select existing access group"
                                                :open-on-focus="true"
                                                @select="addAccessGroup">
                                                <template #empty>
                                                    No existing access group found
                                                </template>
                                            </b-autocomplete>
                                        </b-field>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-tab-item>
                <b-tab-item
                    :visible="libraryEdited.isVirtual"
                    label="Virtual library options">
                    <div class="columns">
                        <div class="column is-three-fifths">
                            <b-notification
                                v-show="virtualLibraryCompilerError"
                                type="is-danger"
                                :closable="false"
                                has-icon
                                role="alert">
                                <b>Query issue</b><br>
                                {{ virtualLibraryCompilerError }}
                            </b-notification>
                            <b-notification
                                v-show="virtualLibraryCompilerWarning"
                                type="is-warning"
                                :closable="false"
                                has-icon
                                role="alert">
                                <b>Potential query issue</b><br>
                                {{ virtualLibraryCompilerWarning }}
                            </b-notification>
                            <b-notification
                                v-for="n in virtualLibraryOptionsValidationResult?.compilerNotifications"
                                :key="n"
                                type="is-info"
                                :closable="false"
                                has-icon
                                role="alert">
                                <b>Please note</b><br>
                                {{ n }}
                            </b-notification>
                            <b-notification
                                v-show="(virtualLibraryOptionsValidationResult?.dependsOnLibraries.length ?? 0) > 0"
                                :closable="false"
                                has-icon
                                icon="graph"
                                role="alert">
                                <b>The source query depends on the following libraries:</b><br>
                                <span
                                    v-for="d in virtualLibraryOptionsValidationResult?.dependsOnLibraries"
                                    :key="d"
                                    class="tag is-info mr-2 is-medium">
                                    {{ d }}
                                </span>
                            </b-notification>
                            <b-field
                                label="Scope column">
                                <b-input
                                    v-model="virtualLibraryOptions.scopeColumn" />
                            </b-field>
                            <b-field
                                label="Source query">
                                <sql-edit-control
                                    v-model="virtualLibrarySourceQuery"
                                    enable-run-shortcut
                                    @run="validateVirtualLibraryOptionsHandler" />
                            </b-field>
                            <b-field>
                                <b-button
                                    type="is-primary"
                                    icon-left="auto-fix"
                                    :loading="virtualLibraryOptionsValidationRunning"
                                    @click="validateVirtualLibraryOptionsHandler">
                                    Validate
                                </b-button>
                            </b-field>
                            <p v-if="virtualLibraryOptions.exists">
                                Virtual Library options last updated {{ longDate(virtualLibraryOptions.dateUpdated) }} UTC.
                            </p>
                            <p v-else>
                                Options not yet saved.
                            </p>
                        </div>
                        <div class="column">
                            <h3 class="title is-5">
                                Defining a virtual library
                            </h3>
                            <p class="block">
                                A virtual library is based on any query (the source query) over the data from other libraries.
                                You may join in any libraries you need, use union, etc.
                            </p>
                            <p class="block">
                                The source query <i>must</i> have the following columns:
                            </p>
                            <ul
                                class="block bullet-list">
                                <li>
                                    <b>Identity</b> - should if possible be a unique string
                                </li>
                                <li>
                                    <b>Name</b>
                                </li>
                            </ul>
                            <p class="block">
                                The following columns are <i>recommended</i>, and may be added automtically if not defined in the query:
                            </p>
                            <ul
                                class="block bullet-list">
                                <li>
                                    <b>Description</b>
                                </li>
                                <li>
                                    <b>IsValid</b> - must be of datatype <code>bit</code>.
                                </li>
                                <li>
                                    <b>DateUpdated</b> - is required if you want to use subscriptions or webhooks. May be added automatically for simple source queries.
                                </li>
                            </ul>
                            <p class="block">
                                One of the columns you add may be used as a <b>scope column</b>. If the scope column is called <code>Facility</code> it can be used as a scope when publishing changes to STID, TIE, etc.
                            </p>
                            <p class="block">
                                When you have prepared the source query, click the <b>Validate</b> button to verify. Then click <b>Save</b> when you are satisfied.
                            </p>
                            <p class="block">
                                Example source query:
                            </p>
                            <pre>
SELECT
   concat_ws('-', pf.facility, pc.Project, c.Name) as Name
  ,concat_ws('|', pf.facility, pc.Project, c.Name) as [Identity]
  ,CONVERT(bit, 1) as IsValid
  ,pf.facility as Facility
  ,p.Name as Project, p.description as ProjectDescription
  ,c.Name as Contract, c.Description as ContractDescription
  ,pc.PIMSDCDomain
  ,GREATEST(pc.DateUpdated, pf.DateUpdated, c.DateUpdated, p.DateUpdated) as DateUpdated
FROM ProjectContract pc
  join ProjectFacility pf on pf.project_id = pc.project_id and pf.IsValid = 1
  join contract c on c.id = pc.Contract_ID
  join project p on p.id = pc.Project_ID
WHERE pc.IsValid = 1
                            </pre>
                        </div>
                    </div>
                </b-tab-item>
                <b-tab-item
                    label="Library definition">
                    <markdown-editor-component
                        v-model="libraryEdited.definition"
                        :show-preview="true" />
                </b-tab-item>
                <b-tab-item
                    label="Attribute definitions">
                    <div class="column is-clearfix">
                        <div class="field is-pulled-right">
                            <p class="control">
                                <b-button
                                    class="is-info"
                                    icon-left="plus-circle-outline"
                                    @click="newAD">
                                    New attribute definition
                                </b-button>
                            </p>
                        </div>
                    </div>
                    <attribute-def-edit
                        ref="attributeEdit"
                        :attributedefs="libraryEdited.attributeDefinitions"
                        :library="libraryEdited"
                        @errorChanged="onAttrErrorChanged" />
                </b-tab-item>
                <b-tab-item
                    label="Governance and documentation">
                    <item-metadata
                        ref="libraryInformationElement"
                        :selected-name="libraryName"
                        :save-button-enabled="false"
                        :save-message-enabled="false"
                        type="libraryInformationElement" />
                </b-tab-item>
            </b-tabs>
        </div>
    </spinner>
</template>
